import { Component } from '@theme/component';

/**
 * A looping explainer video that costs nothing until it is worth playing.
 *
 * The markup ships with a poster image and a <video> that has no src. This
 * component attaches the source only when both are true:
 *
 *   - the visitor has not asked for reduced motion, and
 *   - the element is near the viewport
 *
 * That matters here because the file is 2.6MB sitting below the fold. An
 * `autoplay` attribute would fetch and decode it on every page load, including
 * for people who will never scroll to it, and including for people who have
 * explicitly asked the operating system for less movement — where hiding the
 * video in CSS would still download and play it, just invisibly.
 */
class RytmaVideoComponent extends Component {
  requiredRefs = ['video'];

  #observer;

  connectedCallback() {
    super.connectedCallback();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const src = this.dataset.src;
    if (!src) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#load(src);
      },
      { rootMargin: '200px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  /** @param {string} src */
  #load(src) {
    const video = this.refs.video;
    if (!(video instanceof HTMLVideoElement)) return;

    video.src = src;
    video.load();
    // A rejected play() is normal — some browsers refuse until interaction.
    // The poster stays visible underneath, so there is nothing to recover from.
    video.play().then(
      () => this.setAttribute('data-playing', 'true'),
      () => {}
    );
  }
}

if (!customElements.get('rytma-video-component')) {
  customElements.define('rytma-video-component', RytmaVideoComponent);
}
